import { useEffect, useState } from "react";
import GpsOdometer from "@/components/GpsOdometer";
import { useNavigate, useParams } from "react-router-dom";
import WizardLayout from "@/components/WizardLayout";
import PhotoUploader from "@/components/PhotoUploader";
import SignaturePad from "@/components/SignaturePad";
import InventoryChecklist from "@/components/InventoryChecklist";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatError } from "@/lib/api";
import { allChecked, equipmentOf, FUEL_LEVELS } from "@/lib/equipment";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { eur } from "@/lib/format";

const STEPS = ["Estado", "Limpieza", "Incidencias", "Inventario", "Firma"];

function getPositionSafe() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

export default function ClientCheckout() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [lockStatus, setLockStatus] = useState(null); // null | lock_requested | locked
  const [data, setData] = useState({
    km_start: "",
    km_end: "",
    fuel_level: "lleno",
    interior_clean: true,
    exterior_clean: true,
    black_water_emptied: true,
    grey_water_emptied: true,
    fresh_water_filled: true,
    returned_on_time: true,
    photos: [],
    incidents: "",
    damages: "",
    inventory: {},
    signature: "",
  });

  useEffect(() => {
    api
      .get(`/r/${code}`)
      .then((res) => {
        if (!res.data.has_checkin) {
          toast.error("Debes hacer el check-in primero");
          navigate(`/reserva/${code}`);
          return;
        }
        if (res.data.has_checkout) {
          setDone(true);
        }
        setReservation(res.data);
        setData((d) => ({ ...d, inventory: allChecked(equipmentOf(res.data)) }));
      })
      .catch((err) => {
        toast.error(formatError(err));
        navigate("/");
      });
  }, [code, navigate]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const isStepValid = () => {
    switch (step) {
      case 0:
        return data.km_end !== "" && Number(data.km_end) >= 0 && data.photos.length >= 2;
      case 1:
        return true;
      case 2:
        return true;
      case 3:
        return true;
      case 4:
        return data.signature;
      default:
        return false;
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const resp = await api.post(`/r/${code}/checkout`, {
        ...data,
        km_end: Number(data.km_end),
        km_start: data.km_start === "" ? null : Number(data.km_start),
      });
      setDone(true);
      if (resp.data.extra_charge > 0) {
        toast.warning(`Cargo extra por km: ${eur(resp.data.extra_charge)}`);
      } else {
        toast.success("¡Check-out completado!");
      }
      // Devolución autónoma: avisar al propietario para el cierre remoto
      if (reservation?.self_service) {
        try {
          const location = await getPositionSafe();
          await api.post(`/r/${code}/self-service/request-lock`, { location });
          setLockStatus("lock_requested");
        } catch {
          // el propietario recibe igualmente el aviso de check-out
        }
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  // Devolución autónoma: refrescar hasta que el propietario confirme el cierre
  useEffect(() => {
    if (lockStatus !== "lock_requested") return undefined;
    const t = setInterval(async () => {
      try {
        const { data: s } = await api.get(`/r/${code}/self-service/state`);
        if (s.return?.status === "locked") setLockStatus("locked");
      } catch {
        // siguiente tick
      }
    }, 6000);
    return () => clearInterval(t);
  }, [lockStatus, code]);

  const onNext = () => {
    if (step === STEPS.length - 1) {
      submit();
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const onBack = () => {
    if (step === 0) {
      navigate(`/reserva/${code}`);
      return;
    }
    setStep((s) => Math.max(0, s - 1));
  };

  if (!reservation) return null;

  if (done) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-6">
        <div className="bg-white border border-[#DCD8D0] rounded-2xl p-8 max-w-md w-full text-center fade-in" data-testid="co-completed-screen">
          <div className="h-14 w-14 rounded-full bg-[#4A7C59] text-white flex items-center justify-center mx-auto mb-4">
            <Check className="h-7 w-7" />
          </div>
          <h2 className="font-display text-2xl font-bold text-[#1A1A1A] mb-2">
            ¡Check-out completado!
          </h2>
          <p className="text-[#5C5C5C] mb-6">
            Hemos registrado el estado de devolución. Recibirás la liberación
            de tu fianza en los próximos días tras la inspección.
          </p>
          {reservation?.self_service && (
            <div className="text-left bg-[#E89B33]/10 border border-[#E89B33]/30 rounded-xl p-4 mb-6 text-sm text-[#1A1A1A]" data-testid="co-selfreturn-steps">
              <div className="font-semibold text-[#9C6512] mb-2">Últimos pasos:</div>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Deja la llave <strong>dentro de su funda, en la guantera</strong>.</li>
                <li>Cierra todas las puertas al salir.</li>
                <li>El propietario cerrará la furgoneta a distancia.</li>
              </ol>
              <div className="mt-3 text-xs">
                {lockStatus === "locked" ? (
                  <span className="text-[#4A7C59] font-semibold">✓ Furgoneta cerrada. ¡Ya puedes irte tranquilo!</span>
                ) : lockStatus === "lock_requested" ? (
                  <span className="text-[#9C6512]">Propietario avisado — puedes irte cuando hayas cerrado las puertas.</span>
                ) : null}
              </div>
            </div>
          )}
          <button
            onClick={() => navigate(`/reserva/${code}`)}
            className="bg-marca hover:bg-marca-hover text-white rounded-lg px-6 py-3 font-medium"
            data-testid="co-finish-btn"
          >
            Volver a la reserva
          </button>
        </div>
      </div>
    );
  }

  return (
    <WizardLayout
      title={
        step === 0 ? "Estado de devolución" :
        step === 1 ? "Limpieza y depósitos" :
        step === 2 ? "Incidencias" :
        step === 3 ? "Inventario" :
        "Firma final"
      }
      step={step}
      totalSteps={STEPS.length}
      stepLabel={`Check-out · ${STEPS[step]}`}
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!isStepValid()}
      isLastStep={step === STEPS.length - 1}
      loading={loading}
    >
      {step === 0 && (
        <div className="space-y-5 max-w-2xl">
          <div className="grid grid-cols-2 gap-3">
            {/* Con la entrega registrada, el backend calcula con sus km de salida: no se vuelven a pedir. */}
            {reservation.has_handover ? (
              <div>
                <Label>Km al recoger</Label>
                <div className="mt-2 min-h-9 px-3 py-1.5 flex items-center rounded-md border border-[#DCD8D0] bg-[#F7F5F0] text-sm leading-snug text-[#1A1A1A]" data-testid="co-km-start-readonly">
                  {reservation.handover_km_start != null
                    ? `${Number(reservation.handover_km_start).toLocaleString("es-ES")} km`
                    : "Registrados en la entrega"}
                </div>
              </div>
            ) : (
              <div>
                <Label>Km al recoger <span className="text-[#8A8A8A] text-xs">(opcional)</span></Label>
                <Input type="number" min="0" value={data.km_start} onChange={(e) => set("km_start", e.target.value)} className="mt-2 bg-white border-[#DCD8D0]" placeholder="—" data-testid="co-km-start" />
              </div>
            )}
            <div>
              <Label>Km al devolver</Label>
              <Input type="number" min="0" value={data.km_end} onChange={(e) => set("km_end", e.target.value)} className="mt-2 bg-white border-[#DCD8D0]" data-testid="co-km" />
              <GpsOdometer code={code} onValue={(km) => set("km_end", String(km))} onFuel={(f) => set("fuel_level", f)} current={data.km_end} testId="co-gps" />
            </div>
            <div className="col-span-2">
              <Label>Combustible</Label>
              <Select value={data.fuel_level} onValueChange={(v) => set("fuel_level", v)}>
                <SelectTrigger className="mt-2 bg-white border-[#DCD8D0]" data-testid="co-fuel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FUEL_LEVELS.map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <PhotoUploader
            label="Fotos del vehículo a la devolución (mínimo 2)"
            value={data.photos}
            onChange={(v) => set("photos", v)}
            multiple
            uploadCode={code}
            testid="co-photos"
          />
        </div>
      )}

      {step === 1 && (
        <div className="space-y-3 max-w-xl">
          {[
            ["interior_clean", "Interior limpio"],
            ["exterior_clean", "Exterior limpio"],
            ["black_water_emptied", "Aguas negras (WC) vaciadas"],
            ["grey_water_emptied", "Aguas grises vaciadas"],
            ["fresh_water_filled", "Depósito de agua llenado"],
            ["returned_on_time", "Devuelto a la hora acordada"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center justify-between bg-white border border-[#DCD8D0] rounded-lg p-4 cursor-pointer">
              <span className="text-sm text-[#1A1A1A]">{label}</span>
              <Checkbox checked={data[key]} onCheckedChange={(v) => set(key, Boolean(v))} data-testid={`co-${key}`} />
            </label>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5 max-w-2xl">
          <p className="text-[#5C5C5C]">
            Indica cualquier incidencia o daño detectado durante el viaje.
            Sé honesto: ayuda a resolver mejor cualquier reclamación.
          </p>
          <div>
            <Label>Incidencias</Label>
            <Textarea value={data.incidents} onChange={(e) => set("incidents", e.target.value)} className="mt-2 bg-white border-[#DCD8D0]" rows={4} data-testid="co-incidents" />
          </div>
          <div>
            <Label>Daños o golpes nuevos</Label>
            <Textarea value={data.damages} onChange={(e) => set("damages", e.target.value)} className="mt-2 bg-white border-[#DCD8D0]" rows={4} data-testid="co-damages" />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4 max-w-2xl">
          <p className="text-[#5C5C5C]">
            Confirma que devuelves todos los elementos. Desactiva los que
            falten o estén dañados.
          </p>
          <InventoryChecklist items={equipmentOf(reservation)} value={data.inventory} onChange={(v) => set("inventory", v)} />
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4 max-w-xl">
          <p className="text-[#5C5C5C]">
            Firma para confirmar la devolución del vehículo y los datos
            anteriores.
          </p>
          <SignaturePad value={data.signature} onChange={(v) => set("signature", v)} testid="co-signature" />
        </div>
      )}
    </WizardLayout>
  );
}
